import { forwardRef } from 'react';
import { TextMarkupRenderer } from '@mentimeter/tiptap/text-markup-renderer';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { Text } from '@mentimeter/ragnar-ui/text';
import {
  textHorizontalAlignToAlignItems,
  textVerticalAlignToJustifyContent,
} from '../../utils/textAlignmentToFlexboxAlignment';
import type { TextAlignment } from '../../types';
import { StaggerCss } from '../../shared/components/StaggerCss';
import { canvasBlockStylesExtend } from './styles';

interface FreeTextPresentationProps {
  styledBody: Record<string, unknown>;
  textAlignment: TextAlignment;
  shouldAnimate: boolean;
  CanvasTitleArea: InteractiveCanvasComponents['CanvasTitleArea'];
  CanvasBodyArea: InteractiveCanvasComponents['Body'];
}

export function FreeTextPresentation({
  styledBody,
  textAlignment,
  shouldAnimate,
  CanvasTitleArea,
  CanvasBodyArea,
}: FreeTextPresentationProps) {
  const justifyContent =
    textVerticalAlignToJustifyContent[textAlignment.textVerticalAlign];
  const alignItems =
    textHorizontalAlignToAlignItems[textAlignment.textHorizontalAlign];

  const ConditionalWrapper = CanvasTitleArea ?? NonInteractiveWrapper;

  return (
    <ConditionalWrapper
      width="100%"
      alignItems={alignItems}
      justifyContent={justifyContent}
      height="100%"
    >
      {({ isDisabled, toolbarAnchorRef }) => (
        <Box
          py="space1"
          px="space2"
          width="100%"
          alignItems={alignItems}
          height="100%"
          overflow="hidden"
        >
          <Text
            mb={0}
            textAlign={textAlignment.textHorizontalAlign}
            fontSize={6}
            color="text"
            width="100%"
            extend={canvasBlockStylesExtend}
            flex="1 1 auto"
          >
            <StaggerCss index={1} enableAnimations={shouldAnimate}>
              {CanvasBodyArea ? (
                CanvasBodyArea({
                  entityType: 'static-content',
                  formattedValue: styledBody,
                  isDisabled,
                  justifyContent,
                  placeholder: undefined,
                  toolbarAnchorRef,
                })
              ) : (
                <Box
                  height="100%"
                  alignItems={alignItems}
                  justifyContent={justifyContent}
                >
                  <TextMarkupRenderer
                    allowBlockStyles
                    content={styledBody}
                    editorContentProps={{
                      style: { width: '100%' },
                    }}
                  />
                </Box>
              )}
            </StaggerCss>
          </Text>
        </Box>
      )}
    </ConditionalWrapper>
  );
}

type NonInteractiveWrapperProps = Omit<BoxT, 'children'> & {
  children: ({
    isDisabled,
    toolbarAnchorRef,
  }: {
    isDisabled: boolean;
    toolbarAnchorRef: undefined;
  }) => React.ReactNode;
};
const NonInteractiveWrapper = forwardRef(
  ({ children, ...props }: NonInteractiveWrapperProps, forwardedRef) => (
    <Box p="space4" {...props} ref={forwardedRef}>
      {children({ isDisabled: false, toolbarAnchorRef: undefined })}
    </Box>
  ),
);
